/**
 * Portuguese ISV (Imposto Sobre Veículos) Tax Calculator
 *
 * Official 2026 tax tables for vehicle importation to Portugal
 * Based on government regulations from impostosobreveiculos.info
 *
 * ISV = (Component A + Component B + Surcharges) × (1 - Depreciation)
 *
 * Component A: Engine displacement (cilindrada)
 * Component B: CO2 emissions (componente ambiental)
 * Surcharges: Diesel particulate filter (+€500)
 * Depreciation: Age-based discount (10%-80%)
 */

import { normalizeFuelForDB } from '../utils/car-data.js';

// ============================================
// COMPONENT A: ENGINE DISPLACEMENT
// ============================================

function calculateComponentA(engineCC) {
  if (engineCC <= 1000) {
    return engineCC * 1.09 - 849.03;
  } else if (engineCC <= 1250) {
    return engineCC * 1.18 - 850.69;
  } else {
    // Over 1250cc
    return engineCC * 5.61 - 6194.88;
  }
}

// ============================================
// COMPONENT B: CO2 EMISSIONS
// ============================================

// Gasoline WLTP (2018+ vehicles)
function calculateGasolineWLTP(co2) {
  if (co2 <= 110) return co2 * 0.44 - 43.02;
  if (co2 <= 115) return co2 * 1.10 - 115.80;
  if (co2 <= 120) return co2 * 1.38 - 147.79;
  if (co2 <= 130) return co2 * 5.27 - 619.17;
  if (co2 <= 145) return co2 * 6.38 - 762.73;
  if (co2 <= 175) return co2 * 41.54 - 5819.56;
  if (co2 <= 195) return co2 * 51.38 - 7247.39;
  if (co2 <= 235) return co2 * 193.01 - 34190.52;
  return co2 * 233.81 - 41910.96;
}

// Gasoline NEDC (pre-2018 vehicles)
function calculateGasolineNEDC(co2) {
  if (co2 <= 99) return co2 * 4.62 - 427.00;
  if (co2 <= 115) return co2 * 8.09 - 750.99;
  if (co2 <= 145) return co2 * 52.56 - 5903.94;
  if (co2 <= 175) return co2 * 61.24 - 7140.17;
  if (co2 <= 195) return co2 * 155.97 - 23627.27;
  return co2 * 205.65 - 33390.12;
}

// Diesel WLTP (2018+ vehicles)
function calculateDieselWLTP(co2) {
  if (co2 <= 110) return co2 * 1.72 - 11.50;
  if (co2 <= 120) return co2 * 18.96 - 1906.19;
  if (co2 <= 140) return co2 * 65.04 - 7360.85;
  if (co2 <= 150) return co2 * 127.40 - 16080.57;
  if (co2 <= 160) return co2 * 160.81 - 21176.06;
  if (co2 <= 170) return co2 * 221.69 - 29227.38;
  if (co2 <= 190) return co2 * 274.08 - 36987.98;
  return co2 * 282.35 - 38271.32;
}

// Diesel NEDC (pre-2018 vehicles)
function calculateDieselNEDC(co2) {
  if (co2 <= 79) return co2 * 5.78 - 439.04;
  if (co2 <= 95) return co2 * 23.45 - 1848.58;
  if (co2 <= 120) return co2 * 79.22 - 7195.63;
  if (co2 <= 140) return co2 * 175.73 - 18924.92;
  if (co2 <= 160) return co2 * 195.43 - 21720.92;
  return co2 * 268.42 - 33447.90;
}

function calculateComponentB(co2, fuelType, isNEDC) {
  if (fuelType === 'petrol' || fuelType === 'gasoline') {
    return isNEDC ? calculateGasolineNEDC(co2) : calculateGasolineWLTP(co2);
  } else if (fuelType === 'diesel') {
    return isNEDC ? calculateDieselNEDC(co2) : calculateDieselWLTP(co2);
  } else if (fuelType === 'electric') {
    return 0; // Electric vehicles exempt
  } else if (fuelType === 'hybrid') {
    // Hybrids get 60% discount (pay only 40%)
    const base = isNEDC ? calculateGasolineNEDC(co2) : calculateGasolineWLTP(co2);
    return base * 0.40;
  } else if (fuelType === 'plug-in hybrid' || fuelType === 'phev') {
    // Plug-in hybrids get 75% discount (pay only 25%)
    const base = isNEDC ? calculateGasolineNEDC(co2) : calculateGasolineWLTP(co2);
    return base * 0.25;
  }
  
  // Default to petrol calculation
  return isNEDC ? calculateGasolineNEDC(co2) : calculateGasolineWLTP(co2);
}

// ============================================
// AGE-BASED DEPRECIATION
// ============================================

function getDepreciationRate(age) {
  if (age <= 1) return 0.10;
  if (age <= 2) return 0.20;
  if (age <= 3) return 0.28;
  if (age <= 4) return 0.35;
  if (age <= 5) return 0.43;
  if (age <= 6) return 0.52;
  if (age <= 7) return 0.60;
  if (age <= 8) return 0.65;
  if (age <= 9) return 0.70;
  if (age <= 10) return 0.75;
  return 0.80; // 10+ years = 80% discount
}

// ============================================
// NEDC vs WLTP DETERMINATION
// ============================================

function determineNEDCorWLTP(year) {
  // < 2018: NEDC
  // >= 2018: WLTP
  return year < 2018;
}

// ============================================
// DATA EXTRACTION HELPERS
// ============================================

function extractEngineCC(car) {
  // If we have the actual data, use it
  if (car.engine_cc) return car.engine_cc;
  
  // Otherwise estimate from Porsche Macan model variants
  const modelLower = (car.model || '').toLowerCase();
  
  if (modelLower.includes('turbo')) return 2894;
  if (modelLower.includes('gts')) return 2894;
  if (modelLower.includes('s')) return 2995;
  if (modelLower.includes('macan')) return 1984; // Base model
  
  // Conservative default
  return 2000;
}

function extractCO2(car, isNEDC) {
  // Prefer the emission value matching the car's test standard
  if (isNEDC && car.co2_emissions_nedc) return car.co2_emissions_nedc;
  if (!isNEDC && car.co2_emissions_wltp) return car.co2_emissions_wltp;

  // Fall back to whichever value is available
  if (car.co2_emissions_wltp) return car.co2_emissions_wltp;
  if (car.co2_emissions_nedc) return car.co2_emissions_nedc;

  // Estimate based on fuel type and year
  const fuelType = normalizeFuelType(car.fuel_type);
  const year = car.year || 2020;

  if (fuelType === 'electric') return 0;
  if (fuelType === 'diesel') return year >= 2020 ? 140 : 160;
  if (fuelType === 'petrol') return year >= 2020 ? 180 : 200;

  return 180; // Conservative default
}

// Fuel normalization delegated to shared car-data module, with 'petrol' default for ISV
function normalizeFuelType(fuelType) {
  return normalizeFuelForDB(fuelType) || 'petrol';
}

// ============================================
// MAIN ISV CALCULATION
// ============================================

/**
 * Calculate Portuguese ISV (Vehicle Import Tax)
 * 
 * @param {Object} car - Car object with properties:
 *   - year: Registration year
 *   - fuel_type: Fuel type (petrol/diesel/electric/hybrid)
 *   - engine_cc: Engine displacement (optional)
 *   - co2_emissions_wltp: CO2 emissions under WLTP standard (optional)
 *   - co2_emissions_nedc: CO2 emissions under NEDC standard (optional)
 *   - make: Car manufacturer
 *   - model: Car model
 * @returns {number} ISV tax amount in euros
 */
export function calculatePortugalISV(car) {
  const currentYear = new Date().getFullYear();
  const year = car.year || currentYear;
  const age = Math.max(0, currentYear - year);
  
  const engineCC = extractEngineCC(car);
  const isNEDC = determineNEDCorWLTP(year);
  const co2Emissions = extractCO2(car, isNEDC);
  const fuelType = normalizeFuelType(car.fuel_type);
  
  // Component A: Engine displacement
  const componentA = calculateComponentA(engineCC);
  
  // Component B: CO2 emissions
  const componentB = calculateComponentB(co2Emissions, fuelType, isNEDC);
  
  // Diesel particulate surcharge
  const particleSurcharge = (fuelType === 'diesel') ? 500 : 0;
  
  // Total before depreciation
  const baseISV = componentA + componentB + particleSurcharge;
  
  // Apply age-based depreciation (for imported used cars)
  const depreciationRate = getDepreciationRate(age);
  const depreciation = baseISV * depreciationRate;
  
  const finalISV = Math.max(0, baseISV - depreciation);
  
  // Log detailed breakdown for debugging
  if (console && console.log) {
    console.log(`💰 ISV Calculation for ${car.make} ${car.model} ${year}:`, {
      engineCC,
      co2Emissions,
      fuelType,
      testStandard: isNEDC ? 'NEDC' : 'WLTP',
      age: `${age} years`,
      componentA: `€${Math.round(componentA)}`,
      componentB: `€${Math.round(componentB)}`,
      particleSurcharge: `€${particleSurcharge}`,
      baseISV: `€${Math.round(baseISV)}`,
      depreciationRate: `${(depreciationRate * 100).toFixed(0)}%`,
      depreciation: `€${Math.round(depreciation)}`,
      finalISV: `€${Math.round(finalISV)}`
    });
  }
  
  return Math.round(finalISV);
}

// Export for use in other modules
export default calculatePortugalISV;
